import React, { useState, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import './SuperAdminDashboardLayout.css';

// Date preset options
const DATE_PRESETS = {
  TODAY: 'today',
  LAST_7_DAYS: 'last_7_days',
  LAST_30_DAYS: 'last_30_days',
  MTD: 'mtd',
};

// Routes that need the date selector header (transactions has its own)
const ROUTES_WITH_DATE_HEADER = [
  '/solventpay',
  '/solventpay/overview',
];

const SuperAdminDashboardLayout = () => {
  const location = useLocation();
  const [activePreset, setActivePreset] = useState(DATE_PRESETS.MTD);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Check if current route needs the date header
  const showDateHeader = ROUTES_WITH_DATE_HEADER.includes(location.pathname);

  const calculatePresetRange = (preset) => {
    const now = new Date();
    const today = moment(new Date(now.getFullYear(), now.getMonth(), now.getDate()));

    switch (preset) {
      case DATE_PRESETS.TODAY:
        return [
          today.toDate(),
          today.clone().endOf('day').toDate(),
        ];
      case DATE_PRESETS.LAST_7_DAYS:
        return [
          today.clone().subtract(6, 'days').startOf('day').toDate(),
          today.clone().endOf('day').toDate(),
        ];
      case DATE_PRESETS.LAST_30_DAYS:
        return [
          today.clone().subtract(29, 'days').startOf('day').toDate(),
          today.clone().endOf('day').toDate(),
        ];
      case DATE_PRESETS.MTD:
        return [
          today.clone().startOf('month').toDate(),
          today.clone().endOf('day').toDate(),
        ];
      default:
        return [
          today.clone().startOf('month').toDate(),
          today.clone().endOf('day').toDate(),
        ];
    }
  };

  const dateRange = useMemo(() => {
    // If custom dates are set, use them
    if (customStartDate && customEndDate) {
      return [
        moment(customStartDate).startOf('day').toDate(),
        moment(customEndDate).endOf('day').toDate(),
      ];
    }
    // Otherwise use the active preset
    return calculatePresetRange(activePreset);
  }, [activePreset, customStartDate, customEndDate]);

  // Format date for input value
  const formatForInput = (date) => moment(date).format('YYYY-MM-DD');

  const handlePresetClick = (preset) => {
    setActivePreset(preset);
    // Clear custom dates when selecting a preset
    setCustomStartDate('');
    setCustomEndDate('');
  };

  const handleStartDateChange = (e) => {
    setCustomStartDate(e.target.value);
    setActivePreset(null); // Clear preset when manually changing dates
  };

  const handleEndDateChange = (e) => {
    setCustomEndDate(e.target.value);
    setActivePreset(null); // Clear preset when manually changing dates
  };

  const presetButtons = [
    { key: DATE_PRESETS.TODAY, label: 'Today' },
    { key: DATE_PRESETS.LAST_7_DAYS, label: '7D' },
    { key: DATE_PRESETS.LAST_30_DAYS, label: '30D' },
    { key: DATE_PRESETS.MTD, label: 'MTD' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {showDateHeader && (
        <Box className="dashboard-layout-header">
          <Typography variant="h4" component="h1" className="dashboard-layout-title">
            SolventPay
          </Typography>
          <div className="date-range-picker-layout">
            <div className="date-inputs-layout">
              <div className="date-input-wrapper-layout">
                <svg className="date-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <input
                  type="date"
                  className="date-input-layout"
                  value={customStartDate || formatForInput(dateRange[0])}
                  onChange={handleStartDateChange}
                />
              </div>
              <span className="date-separator-layout">→</span>
              <div className="date-input-wrapper-layout">
                <input
                  type="date"
                  className="date-input-layout date-input-end"
                  value={customEndDate || formatForInput(dateRange[1])}
                  onChange={handleEndDateChange}
                />
              </div>
            </div>
            <div className="date-presets-layout">
              {presetButtons.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  className={`preset-pill-layout ${activePreset === key ? 'active' : ''}`}
                  onClick={() => handlePresetClick(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </Box>
      )}
      <Outlet context={{ dateRange }} />
    </Box>
  );
};

export default SuperAdminDashboardLayout;
